import { ArrowDown } from "lucide-react";
import { Button, Section, withHighlight } from "@/components/ui";

/**
 * Industry hero: breadcrumb, H1, one-line promise and two quick facts on the left,
 * a teen + mentor cut-out on a tinted stage on the right (stage colours come from the industry theme).
 * Text comes from src/data/industryContent.js only.
 */
export function IndustryHero({ industry, content }) {
  const { theme, cutout } = industry;
  const { crumb, h1, h1Highlight, sub, facts } = content;

  return (
    <Section tone="brandTint" testId="industry-hero" spacing="compact" labelledBy="industry-title">
      <div className="relative z-10">
        <nav aria-label="Breadcrumb" className="mb-5 sm:mb-6">
          <p className="text-sm text-ink/70">
            Industries <span aria-hidden="true">/</span> <b className="font-semibold text-ink">{crumb}</b>
          </p>
        </nav>

        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14">
          <div className="space-y-5 sm:space-y-6 lg:col-span-7">
            <h1 id="industry-title" className="font-heading text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
              {withHighlight(h1, h1Highlight)}
            </h1>
            <p className="max-w-[44ch] text-lg leading-relaxed text-ink/80 sm:text-xl">{sub}</p>

            {facts.length > 0 ? (
              <ul className="flex flex-wrap gap-2" data-testid="industry-facts">
                {facts.map((fact) => (
                  <li
                    key={fact}
                    className="rounded-full border border-brand-200 bg-white px-4 py-1.5 text-sm font-semibold sm:text-base"
                  >
                    {fact}
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="pt-1">
              <Button href="#programmes" size="lg" data-testid="industry-view-programs">
                {content?.programmes?.title ?? "Programmes"}
                <ArrowDown className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5" data-testid="industry-hero-stage">
            <div
              className={`relative mx-auto h-[22rem] max-w-md overflow-hidden rounded-5xl border sm:h-[26rem] lg:h-[clamp(22rem,52vh,30rem)] lg:max-w-none ${theme.stage}`}
            >
              <span
                aria-hidden="true"
                className="absolute left-1/2 top-[48%] h-[78%] w-[88%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/70"
              />
              <img
                src={`${process.env.PUBLIC_URL}/${cutout.file}`}
                alt={cutout.alt}
                width={532}
                height={1024}
                fetchPriority="high"
                decoding="async"
                className="absolute bottom-0 left-1/2 h-[94%] w-auto -translate-x-1/2 object-contain object-bottom"
              />
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
